import React from 'react'
import { useApp } from '../../context/AppContext'
import { Icon, useT } from '../../shared'
import BrandLogo from '../../shared/BrandLogo'
import { getNav, groupNav } from './nav'
import ContextCard from './ContextCard'
import { useCollapsedGroups } from './useCollapsedGroups'
import { useFavorites } from './useFavorites'
import { useNavBadges } from './useNavBadges'
import NavGroup, { NavItem } from './NavGroup'

// Arti angka di tiap menu, untuk tooltip dan pembaca layar. Id yang tak ada di
// sini tetap menampilkan angkanya, hanya tanpa keterangan.
const BADGE_HINTS = {
  'cert-send': 'badgeCertSend',
  'doc-library': 'badgeDocLibrary',
  penilaian: 'badgePenilaian',
}

export default function Sidebar() {
  const { role, page, navigate, lang, user, logout, branding, sidebarCollapsed: rail, toggleSidebar, currentProgramId, currentClassId } = useApp()
  const t = useT()
  // Angka "menunggu tindakan" (cert-send, doc-library, penilaian) — hanya admin.
  const badges = useNavBadges({ enabled: role === 'admin', programId: currentProgramId, classId: currentClassId, page })
  const nav = getNav(role, t, lang, { superAdmin: user?.role === 'SUPER_ADMIN' }).map((item) => {
    const n = item.id ? badges[item.id] : 0
    if (!(n > 0)) return item
    return { ...item, badge: n > 99 ? '99+' : String(n), badgeHint: `${n} ${t[BADGE_HINTS[item.id]] || ''}`.trim() }
  })
  const groupsState = useCollapsedGroups(role)
  const { favs, toggle: toggleFavorite } = useFavorites(role)

  const initials = user?.fullName
    ? user.fullName.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()
    : '??'

  // FR-28: platform staff are NOT institution admins, and labelling them "Admin"
  // blurs the one distinction the whole tenancy model rests on — a SUPER_ADMIN is
  // super inside their own institution only.
  const roleLabel = role === 'participant' ? (lang === 'id' ? 'Peserta' : 'Participant')
    : role === 'trainer' ? 'Trainer'
    : role === 'platform' ? (lang === 'id' ? 'Pemilik Platform' : 'Platform Owner')
    : 'Admin'

  const { groups: navGroups, footerItems } = groupNav(nav)

  // Grup Favorit: salinan butir yang ditandai bintang, di bawah butir lead
  // (Beranda). Id yang tak ada lagi di menu peran ini diabaikan. Di rail ikon
  // grup ini dilewati: ikon yang sama akan muncul dua kali tanpa judul pembeda.
  const allItems = [...navGroups.flatMap((g) => g.items), ...footerItems]
  const favItems = favs.map((id) => allItems.find((item) => item.id === id)).filter(Boolean)
  const groups = [...navGroups]
  if (favItems.length && !rail) {
    const at = groups[0]?.key === '_lead' ? 1 : 0
    groups.splice(at, 0, { key: 'favorites', title: t.navFavorites.toUpperCase(), items: favItems, variant: 'favorite' })
  }

  // Pindah ke halaman di grup yang sedang dilipat (lewat pencarian, tautan di
  // dasbor, tombol kembali) membuka grup itu, supaya butir aktif tidak tersembunyi.
  // Hanya bereaksi pada pergantian halaman: melipat grup aktif secara sengaja
  // tetap dihormati.
  React.useEffect(() => {
    // Semua grup yang memuat halaman ini (termasuk Favorit, yang menyalin butirnya).
    groups
      .filter((grp) => grp.items.some((item) => item.id === page))
      .forEach((grp) => { if (groupsState.isClosed(grp.key)) groupsState.open(grp.key) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const collapseLabel = rail ? t.navExpand : t.navCollapse

  return (
    <aside className="sidebar">
      {/* Brand — the institution's, not the platform's. This used to be
          hardcoded to OSCT, so every institution's staff worked inside a
          sidebar labelled "Oil Spill Combat Training". */}
      <div className="sidebar-brand" onClick={() => navigate('dashboard')} title={rail ? (branding?.institution_name || 'Gladhy') : undefined}>
        <BrandLogo size={32} />
        <div className="brand-text">
          <div className="brand-name">{branding?.institution_name || 'Gladhy'}</div>
          <div className="brand-sub">{branding?.footer_text || (branding?.hide_platform_branding ? '' : t.appNameSub)}</div>
        </div>
      </div>

      <ContextCard />

      {/* Navigation */}
      <nav className="sidebar-nav" style={{ flex: 1, overflowY: 'auto', paddingBottom: 8 }}>
        {groups.map((group) => (
          <NavGroup
            key={group.key}
            group={group}
            page={page}
            rail={rail}
            closed={groupsState.isClosed(group.key)}
            onToggle={() => groupsState.toggle(group.key)}
            onNavigate={navigate}
            favs={favs}
            onToggleFavorite={toggleFavorite}
          />
        ))}
      </nav>

      {footerItems.length > 0 && (
        <div className="nav-section nav-section-footer">
          {footerItems.map((item) => (
            <NavItem
              key={item.id}
              item={item}
              active={page === item.id}
              rail={rail}
              onNavigate={navigate}
              favorite={favs.includes(item.id)}
              onToggleFavorite={toggleFavorite}
            />
          ))}
        </div>
      )}

      {/* User chip footer */}
      <div className="sidebar-footer">
        <button
          type="button"
          className="sidebar-collapse-btn"
          onClick={toggleSidebar}
          aria-pressed={rail}
          title={collapseLabel}
          aria-label={collapseLabel}
        >
          <Icon name={rail ? 'chevron_right' : 'chevron_left'} size={14} />
          <span className="nav-label">{collapseLabel}</span>
        </button>
        {/* Keluar punya tombol sendiri: dulu seluruh kartu memicu logout, sehingga
            klik pada nama saja sudah mengakhiri sesi. */}
        <div className="user-chip" title={user?.fullName || undefined}>
          <div className="avatar">{initials}</div>
          <div className="user-chip-text">
            <div className="user-chip-name text-truncate">{user?.fullName || '—'}</div>
            <div className="user-chip-role">{roleLabel}</div>
          </div>
          <button type="button" className="user-chip-logout" onClick={logout} title={t.logout} aria-label={t.logout}>
            <Icon name="logout" size={15} />
          </button>
        </div>
      </div>
    </aside>
  )
}
